import { Switch } from '@headlessui/react';
import ReactTooltip from 'react-tooltip';
import { FC, useState } from 'react';
import useClinic from 'hooks/useClinic';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import useMatchMutate from 'hooks/useMatchMutate';
import axiosInstance from 'apis/axiosInstance';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import { renderToast } from 'components/Toast/Toast';
import Table from './Table/Table';

const defaultTableSortState = {
  sortBy: 'name',
  sort: 'desc' as const,
};

interface InsurancePageProps {}

const InsurancePage: FC<InsurancePageProps> = () => {
  const { data: clinic, isLoading: isClinicLoading, mutate } = useClinic();

  const [sort, setSort] = useState<'asc' | 'desc'>(defaultTableSortState.sort);

  const [sortBy, setSortBy] = useState(defaultTableSortState.sortBy);

  const [lastSortInfo, setLastSortInfo] = useState<{
    sort: 'asc' | 'desc';
    sortBy: string;
  }>({
    sort: defaultTableSortState.sort,
    sortBy: defaultTableSortState.sortBy,
  });

  const matchMutate = useMatchMutate();

  const onInsuranceSettingChanged = async (data: {
    [key: string]: boolean;
  }) => {
    try {
      await axiosInstance.patch('/clinics/insurance-setting', data);

      await mutate();
      await matchMutate(/\/admin\/insurances\?[\s\S]+/);
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  if (isClinicLoading) {
    return (
      <div data-testid="page-insurance">
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div
      className="pt-2.4 pl-3 pr-4 text-14 leading-[2.1rem]"
      data-testid="page-insurance"
    >
      <div className="flex flex-row items-center justify-between">
        <h3 className="text-24 font-bold leading-[3.6rem] self-start">
          Insurance Providers
        </h3>

        <div className="flex flex-col gap-y-1.6">
          <div className="flex flex-row gap-x-[2.4rem] items-center justify-between ">
            <div className="flex flex-row items-center gap-x-0.8">
              <span>Require insurance</span>
              <button data-for="requiredInsuranceInfo" data-tip="">
                <InfoIcon className="w-1.6 h-1.6 all-child:fill-mid-grey" />
              </button>
              <ReactTooltip
                id="requiredInsuranceInfo"
                effect="solid"
                place="bottom"
                className="!bg-white !rounded-[0.8rem] !w-[21rem] !h-[auto] z-[9999] flex justify-center items-center !opacity-100 shadow-elevation-07 after:hidden before:hidden !px-[10px] !py-[4px]"
              >
                <span className="text-11 text-darkest-grey flex items-center font-normal">
                  Patients are asked for insurance during booking. Turn off to
                  allow booking without insurance.
                </span>
              </ReactTooltip>
            </div>

            <Switch
              onChange={() =>
                onInsuranceSettingChanged({
                  isInsuranceEnabled: !clinic.isInsuranceEnabled,
                })
              }
              checked={clinic.isInsuranceEnabled}
              className={`${
                clinic.isInsuranceEnabled ? 'bg-magenta' : 'bg-[#78788029]'
              } relative inline-flex h-3.1 w-5.1 items-center rounded-full`}
            >
              <span
                className={`${
                  clinic.isInsuranceEnabled
                    ? 'translate-x-[2.3rem]'
                    : 'translate-x-[2px]'
                } inline-block h-2.5 w-2.5 transform rounded-full transition bg-white`}
              />
            </Switch>
          </div>

          {clinic.isInsuranceEnabled && (
            <div className="flex flex-row gap-x-[2.4rem] items-center justify-between">
              <div className="flex flex-row items-center gap-x-0.8">
                <span>Require additional info</span>
              </div>

              <Switch
                onChange={() =>
                  onInsuranceSettingChanged({
                    isInsuranceRequired: !clinic.isInsuranceRequired,
                  })
                }
                checked={clinic.isInsuranceRequired}
                className={`${
                  clinic.isInsuranceRequired ? 'bg-magenta' : 'bg-[#78788029]'
                } relative inline-flex h-3.1 w-5.1 items-center rounded-full`}
              >
                <span
                  className={`${
                    clinic.isInsuranceRequired
                      ? 'translate-x-[2.3rem]'
                      : 'translate-x-[2px]'
                  } inline-block h-2.5 w-2.5 transform rounded-full transition bg-white`}
                />
              </Switch>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3.2">
        <div className="mb-2.4">
          Select which insurance providers your clinic accepts for online
          booking
        </div>

        <Table
          sort={sort}
          setSort={setSort}
          sortBy={sortBy}
          setSortBy={setSortBy}
          lastSortInfo={lastSortInfo}
          setLastSortInfo={setLastSortInfo}
        />
      </div>
    </div>
  );
};

export default InsurancePage;
